import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { listPosts, getTags, getCategories, searchPosts, getPublicSiteSettings } from '@/features/blog/services/blogApi';
import type { SearchResponse, PublicSiteSettings } from '@/features/blog/services/blogApi';
import type { PaginatedResponse, Post, Tag, Category } from '@/shared/types/api';
import PostCard from '@/features/blog/components/PostCard';
import Pagination from '@/shared/components/Pagination';
import { PostCardSkeleton } from '@/shared/components/Skeleton';
import { useSearchStore } from '@/shared/stores/searchStore';

export default function BlogListPage() {
  const { data: settings } = useQuery<PublicSiteSettings>({
    queryKey: ['site-settings'],
    queryFn: getPublicSiteSettings,
    staleTime: 60_000,
  });

  const [page, setPage] = useState(1);
  const { isOpen: searchOpen, close: closeSearch } = useSearchStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState('');
  const [searchPage, setSearchPage] = useState(1);
  const inputRef = useRef<HTMLInputElement>(null);

  const maintenance = settings?.blog_maintenance_mode ?? false;

  const { data, isLoading, error } = useQuery<PaginatedResponse<Post>>({
    queryKey: ['posts', 'list', page],
    queryFn: () => listPosts({ page, perPage: 10 }),
    enabled: !submittedQuery && !maintenance,
  });

  const { data: tags } = useQuery<Tag[]>({
    queryKey: ['tags'],
    queryFn: getTags,
  });

  const { data: categories } = useQuery<Category[]>({
    queryKey: ['categories'],
    queryFn: getCategories,
  });

  const { data: searchData, isLoading: searchLoading } = useQuery<SearchResponse>({
    queryKey: ['search', submittedQuery, searchPage],
    queryFn: () => searchPosts(submittedQuery, searchPage),
    enabled: submittedQuery.length >= 2,
  });

  useEffect(() => {
    if (searchOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [searchOpen]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim().length >= 2) {
      setSubmittedQuery(searchQuery.trim());
      setSearchPage(1);
    }
  };

  const handleCloseSearch = () => {
    closeSearch();
    setSearchQuery('');
    setSubmittedQuery('');
    setSearchPage(1);
  };

  const isSearching = !!submittedQuery;
  const posts = isSearching ? searchData?.posts : data?.posts;
  const loading = isSearching ? searchLoading : isLoading;

  return (
    <div>
      <Helmet>
        <title>Blog — Vladyslav Chebotarov</title>
        <meta name="description" content="Articles about software development, iOS, architecture, and technology by Vladyslav Chebotarov." />
        <link rel="canonical" href="https://codder.cc/blog" />
        <meta property="og:title" content="Blog — Vladyslav Chebotarov" />
        <meta property="og:description" content="Articles about software development, iOS, architecture, and technology." />
        <meta property="og:url" content="https://codder.cc/blog" />
        <meta property="og:type" content="website" />
        <link rel="alternate" type="application/rss+xml" title="Blog RSS Feed" href="https://codder.cc/api/v1/blog/feed.xml" />
      </Helmet>

      <div className="mb-6 pb-4" style={{ borderBottom: '1px solid var(--color-border)' }}>
        {searchOpen ? (
          <form onSubmit={handleSearchSubmit} className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search posts..."
              className="flex-1 px-2 py-1 border border-border text-sm font-mono focus:outline-none focus:border-accent"
            />
            <button type="submit" className="btn-terminal text-xs">
              Search
            </button>
            <button
              type="button"
              onClick={handleCloseSearch}
              aria-label="Close search"
              className="text-muted text-xs hover:text-accent font-mono bg-transparent border-none cursor-pointer"
            >
              Close
            </button>
          </form>
        ) : (
          <div className="flex flex-wrap gap-2 items-center">
            {categories?.map((cat) => (
              <Link key={`cat-${cat.id}`} to={`/category/${cat.id}/${cat.slug}`} className="text-muted text-sm no-underline hover:underline">
                [{cat.name}]
              </Link>
            ))}
            {tags?.map((tag) => (
              <Link key={tag.id} to={`/tag/${tag.id}/${tag.slug}`} className="text-accent text-sm no-underline hover:underline">
                #{tag.name}
              </Link>
            ))}
          </div>
        )}
      </div>

      {maintenance ? (
        <div
          className="py-10 px-6 text-center border border-border rounded"
          style={{ backgroundColor: 'rgba(255,168,106,0.04)' }}
        >
          <div className="text-4xl mb-4">🛡</div>
          <h3 className="text-base font-bold text-foreground mb-2">{settings!.blog_maintenance_title}</h3>
          <p className="text-sm text-muted">{settings!.blog_maintenance_text}</p>
        </div>
      ) : (
        <>
          {isSearching && searchData && (
            <p className="text-sm text-muted mb-4">
              {searchData.total} result{searchData.total !== 1 ? 's' : ''} for "{submittedQuery}"
            </p>
          )}

          {loading && (
            <>
              <PostCardSkeleton />
              <PostCardSkeleton />
              <PostCardSkeleton />
            </>
          )}

          {!loading && error && !isSearching && (
            <div className="text-center py-12">
              <p className="text-muted text-sm mb-4">Failed to load posts.</p>
              <button onClick={() => window.location.reload()} className="btn-terminal text-xs">
                Retry
              </button>
            </div>
          )}

          {!loading && posts && posts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted text-sm">{isSearching ? 'No posts found.' : 'No posts yet.'}</p>
            </div>
          )}

          {posts?.map((post) => (
            <PostCard key={post.id} post={post} />
          ))}

          {!isSearching && data && (
            <Pagination
              page={data.page}
              totalPages={data.total_pages}
              onPageChange={setPage}
            />
          )}

          {isSearching && searchData && searchData.total_pages > 1 && (
            <Pagination
              page={searchData.page}
              totalPages={searchData.total_pages}
              onPageChange={setSearchPage}
            />
          )}
        </>
      )}
    </div>
  );
}
